import { useModalState } from "@clipboard-health/ui-components";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useRef } from "react";

import { useToast } from "../../../lib";
import { AddSsnBottomSheet } from "../../Ssn/components/AddSsnBottomSheet";
import {
  getIncompleteRequirementPrerequisiteActions,
  PRE_REQUIREMENT_ACTION_KEYS,
} from "../utils/getIncompleteRequirementPrerequisiteActions";
import { useRequirementDetailsContext } from "./useRequirementDetailsContext";

export function useRequirementPrerequisiteActions() {
  const worker = useDefinedWorker();
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const addSsnModalState = useModalState();
  const pendingActionRef = useRef<() => void | Promise<void>>();
  const { showSuccessToast } = useToast();

  const incompleteActions = getIncompleteRequirementPrerequisiteActions({
    worker,
    workerRequirement,
  });

  const incompleteActionKeys = incompleteActions.map((action) => action.actionKey);

  const hasSsnPrerequisite = incompleteActions.some(
    (action) => action.actionKey === PRE_REQUIREMENT_ACTION_KEYS.SSN_REQUIRED
  );

  useLogEffect(
    APP_V2_USER_EVENTS.SSN_PREREQUISITE_DETECTED,
    {
      requirementId: workerRequirement.requirementId,
      incompleteActions: incompleteActionKeys,
    },
    { enabled: hasSsnPrerequisite }
  );

  const withPrerequisiteCheck = useCallback(
    (action: () => void | Promise<void>) => {
      return async () => {
        if (hasSsnPrerequisite) {
          pendingActionRef.current = action;
          addSsnModalState.openModal();
          return;
        }

        await action();
      };
    },
    [hasSsnPrerequisite, addSsnModalState]
  );

  const handleSsnAdded = useCallback(async () => {
    const eventPayload = {
      requirementId: workerRequirement.requirementId,
      incompleteActions: incompleteActionKeys,
    };

    showSuccessToast("Success. Please complete the document.");
    logEvent(APP_V2_USER_EVENTS.SSN_PREREQUISITE_ACTION_STARTED, eventPayload);
    try {
      await pendingActionRef.current?.();
    } finally {
      pendingActionRef.current = undefined;
    }

    logEvent(APP_V2_USER_EVENTS.SSN_PREREQUISITE_ACTION_COMPLETED, eventPayload);
  }, [incompleteActionKeys, showSuccessToast, workerRequirement.requirementId]);

  const prerequisiteBottomSheet = (
    <AddSsnBottomSheet
      title="SSN required to complete this document"
      modalState={addSsnModalState}
      onSsnAdded={handleSsnAdded}
    />
  );

  return {
    withPrerequisiteCheck,
    prerequisiteBottomSheet,
  };
}
